import React, { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';

import Text from '../Text';
import Button from '../Button';
import Checkbox from '../Checkbox';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_M, FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    visible: boolean;
    // Translated via the core Text component (i18n keys or plain text).
    title: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    // Tints the confirm button with the alert color for irreversible actions.
    destructive?: boolean;
    // When set, the user must tick a consent checkbox carrying this label before confirming.
    consentLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
};

// Centered confirm dialog — themed and translatable replacement for Alert.alert, consistent
// across iOS/Android/web.
export default function ConfirmDialog({
    visible,
    title,
    message,
    confirmLabel = 'confirm',
    cancelLabel = 'cancel',
    destructive,
    consentLabel,
    onConfirm,
    onCancel,
}: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const [consented, setConsented] = useState(false);

    // Callers that keep the dialog mounted between openings would otherwise carry a stale tick over
    // into the next decision.
    useEffect(() => {
        if (visible) {
            setConsented(false);
        }
    }, [visible]);

    const confirmBlocked = Boolean(consentLabel) && !consented;

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
            <View style={styles.backdrop}>
                <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
                <View style={styles.card}>
                    <Text fontType="LATO_BOLD" style={styles.title}>
                        {title}
                    </Text>
                    {message ? <Text style={styles.message}>{message}</Text> : null}
                    {consentLabel ? (
                        <Pressable style={styles.consentRow} onPress={() => setConsented(!consented)}>
                            <Checkbox checked={consented} setChecked={setConsented} />
                            <Text style={styles.consentLabel}>{consentLabel}</Text>
                        </Pressable>
                    ) : null}
                    <View style={styles.buttonsRow}>
                        <View style={styles.buttonWrap}>
                            <Button
                                title={cancelLabel}
                                onPress={onCancel}
                                backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                                titleColor={theme.text}
                            />
                        </View>
                        <View style={styles.buttonWrap}>
                            <Button
                                title={confirmLabel}
                                onPress={onConfirm}
                                disabled={confirmBlocked}
                                backgroundColor={destructive ? { backgroundColor: theme.alert } : undefined}
                            />
                        </View>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        backdrop: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
        },
        card: {
            backgroundColor: theme.background,
            borderRadius: scaleSize(12),
            paddingHorizontal: scaleSize(18),
            paddingVertical: scaleSize(18),
            width: 360,
            maxWidth: '88%',
        },
        title: {
            fontSize: FONT_SIZE_M,
            color: theme.text,
        },
        message: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginTop: scaleSize(8),
        },
        consentRow: {
            flexDirection: 'row',
            alignItems: 'flex-start',
            marginTop: scaleSize(14),
        },
        consentLabel: {
            flex: 1,
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            marginLeft: scaleSize(10),
        },
        buttonsRow: {
            flexDirection: 'row',
            marginTop: scaleSize(16),
        },
        buttonWrap: {
            flex: 1,
            marginHorizontal: scaleSize(4),
        },
    });
